import {
  Box,
  Paper,
  Typography,
  Chip,
} from "@mui/material";
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { PieChart, Pie as RechartsPie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { heritageColors } from '../../../../heritageTheme.ts';

const RADIAN = Math.PI / 180;

// פונקציית עזר לציור הקווים והטקסט (Spider Labels)
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name, value, payload }) => {
  if (!cx || !cy || !payload) return null;

  const color = payload.color;
  const sin = Math.sin(-midAngle * RADIAN);
  const cos = Math.cos(-midAngle * RADIAN);
  const sx = cx + (outerRadius + 8) * cos;
  const sy = cy + (outerRadius + 8) * sin;
  const mx = cx + (outerRadius + 20) * cos;
  const my = cy + (outerRadius + 20) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 18;
  const ey = my;
  const textAnchor = cos >= 0 ? 'start' : 'end';

  return (
    <g>
      {/* הקו המחבר */}
      <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={color} fill="none" strokeWidth="2" />

      {/* עיגול קטן בקצה */}
      <circle cx={ex} cy={ey} r={2} fill={color} stroke="none" />

      {/* הטקסט (שם הקטגוריה) */}
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 8}
        y={ey}
        textAnchor={textAnchor}
        fill="#3E2723"
        fontSize="16"
        fontWeight="bold"
        dominantBaseline="central"
      >
        {name}
      </text>

      {/* הסכום מתחת לשם */}
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 8}
        y={ey}
        dy={16}
        textAnchor={textAnchor}
        fill="#3E2723"
        fontSize="18"
        fontWeight="bold"
        dominantBaseline="central"
      >
        {`₪${value.toLocaleString()}`}
      </text>
    </g>
  );
};

const Bar = ({ data = { categories: [] }, freeText = '', EXPENSES_ANALYTICS_WIDTH, EXPENSES_ANALYTICS_HEIGHT, title = 'פילוח הוצאות', showTitle = true }) => {
  const expenses = data.categories || [];

  const total = expenses.reduce((sum, exp) => sum + exp.amount, 0);

  return (
    <Paper
      elevation={0}
      sx={{
        width: `${EXPENSES_ANALYTICS_WIDTH}px`,
        minHeight: `${EXPENSES_ANALYTICS_HEIGHT}px`,
        direction: 'rtl',
        borderRadius: 4,
        overflow: 'visible',
        position: 'relative',
        p: 3,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header */}
      {showTitle && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUpIcon sx={{ color: heritageColors.teal, fontSize: 24 }} />
            <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', color: heritageColors.navy }}>
              {title}
            </Typography>
          </Box>
          <Chip
            label={`סה"כ: ₪${total.toLocaleString()}`}
            sx={{
              bgcolor: heritageColors.creamLight,
              color: heritageColors.navy,
              fontWeight: 700,
              fontSize: '0.95rem',
              border: `1px solid ${heritageColors.border}`,
            }}
          />
        </Box>
      )}

      {/* Pie Chart - גובה קבוע */}
      <Box sx={{ width: '100%', height: '300px', direction: 'ltr', overflow: 'visible', py: 1 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 20, right: 70, bottom: 20, left: 70 }}>
            <RechartsPie
              data={expenses}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomizedLabel}
              outerRadius={80}
              innerRadius={50}
              fill="#8884d8"
              dataKey="amount"
              paddingAngle={2}
              isAnimationActive={false}
            >
              {expenses.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} stroke={heritageColors.white} strokeWidth={2} />
              ))}
            </RechartsPie>
            <Tooltip
              contentStyle={{
                backgroundColor: heritageColors.creamLight,
                border: `1px solid ${heritageColors.border}`,
                borderRadius: '4px',
                color: heritageColors.navy
              }}
              formatter={(value) => `₪${value.toLocaleString()}`}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>

      {/* Free text at the bottom - מתרחב כלפי מטה */}
      {freeText && (
        <Box sx={{
          textAlign: 'center',
          fontSize: '1.2rem',
          color: heritageColors.navy,
          fontWeight: 700,
          mt: 2,
          whiteSpace: 'pre-wrap',
          lineHeight: 1.4
        }}>
          {freeText}
        </Box>
      )}
    </Paper>
  );
};

export default Bar;